"use client";

import { useEffect } from "react";
import {
  FlickerProbeEvent,
  LogLevel,
  MOBILE_TEAM_MODAL_DEBUG_FEATURE,
  MOBILE_TEAM_MODAL_DEBUG_INSTANCE_ID,
  MobileModalDebugSource,
  type PersistentDebugLogger,
  useFlickerProbeLogger,
  useLandingPerfObserver,
  useMountProbe,
  usePersistentDebugLogger,
  useRenderChangeProbe,
  useSeqLog,
} from "@/hooks/telemetry";

const CLICK_INVESTIGATION_ID =
  "click-investigation--team-stack-card--2026-05-25";
const HOMEPAGE_CLICK_INV_COMPONENT = "HomePage";
const LANDING_PERF_ROUTE = "/";
const LANDING_PERF_VARIANT_BASELINE = "baseline";
const LANDING_PERF_SEQ_PLAN = "landing-perf-2026-05-27";
const LANDING_PERF_HOMEPAGE_COMPONENT = "LandingPerf.HomePage";
const LANDING_PERF_PREPAGE_COMPONENT = "LandingPerf.Cprepage";
const LANDING_PERF_HOMEPAGE_MOUNTED_MESSAGE = "homepage-mounted";
const HOMEPAGE_MOBILE_MODAL_DEBUG_COMPONENT = "HomePage/mobile-modal-stack";

type HomePageSeqLogLocal = ReturnType<typeof useSeqLog>;

interface HomePageTelemetryLocal {
  clickInvestigationLog: HomePageSeqLogLocal;
  landingPerfPrepageLog: HomePageSeqLogLocal;
  modalDebugLogger: PersistentDebugLogger;
}

interface HomePageFlickerProbeInput {
  hasOpenedTeamDetailsModal: boolean;
  loading: boolean;
  prepageMinElapsed: boolean;
  shouldRenderPrepage: boolean;
  showAuthModal: boolean;
  showPrepage: boolean;
  showPricingModal: boolean;
  showTeamDetailsModal: boolean;
  showTeamsModal: boolean;
  teamDetailsTeamId: string | null;
  userId: string | null;
  viewport: string | null;
  viewportLoaded: boolean;
}

export function useHomePageTelemetry(): HomePageTelemetryLocal {
  const clickInvestigationLog = useSeqLog(HOMEPAGE_CLICK_INV_COMPONENT, {
    investigation: CLICK_INVESTIGATION_ID,
  });

  useLandingPerfObserver({
    route: LANDING_PERF_ROUTE,
    variant: LANDING_PERF_VARIANT_BASELINE,
    seqPlan: LANDING_PERF_SEQ_PLAN,
  });
  const landingPerfHomePageLog = useSeqLog(LANDING_PERF_HOMEPAGE_COMPONENT, {
    route: LANDING_PERF_ROUTE,
    variant: LANDING_PERF_VARIANT_BASELINE,
    seqPlan: LANDING_PERF_SEQ_PLAN,
  });
  const landingPerfPrepageLog = useSeqLog(LANDING_PERF_PREPAGE_COMPONENT, {
    route: LANDING_PERF_ROUTE,
    variant: LANDING_PERF_VARIANT_BASELINE,
    seqPlan: LANDING_PERF_SEQ_PLAN,
  });
  const modalDebugLogger = usePersistentDebugLogger({
    component: HOMEPAGE_MOBILE_MODAL_DEBUG_COMPONENT,
    instanceId: MOBILE_TEAM_MODAL_DEBUG_INSTANCE_ID,
    metadata: {
      feature: MOBILE_TEAM_MODAL_DEBUG_FEATURE,
      source: MobileModalDebugSource.HomePage,
    },
  });

  useEffect(() => {
    landingPerfHomePageLog(
      LANDING_PERF_HOMEPAGE_MOUNTED_MESSAGE,
      {
        mountedMs: performance.now(),
        navigationStartMs: performance.timeOrigin,
      },
      LogLevel.Info,
    );
  }, [landingPerfHomePageLog]);

  return {
    clickInvestigationLog,
    landingPerfPrepageLog,
    modalDebugLogger,
  };
}

export function useHomePageFlickerProbe(
  input: HomePageFlickerProbeInput,
): void {
  const flickerLogger = useFlickerProbeLogger("HomePage/flicker");
  useMountProbe({ logger: flickerLogger, label: "HomePage" });
  useRenderChangeProbe({
    logger: flickerLogger,
    eventName: FlickerProbeEvent.Render,
    snapshot: {
      source: HOMEPAGE_CLICK_INV_COMPONENT,
      showTeamDetailsModal: input.showTeamDetailsModal,
      teamDetailsTeamId: input.teamDetailsTeamId,
      hasOpenedTeamDetailsModal: input.hasOpenedTeamDetailsModal,
      showPrepage: input.showPrepage,
      shouldRenderPrepage: input.shouldRenderPrepage,
      prepageMinElapsed: input.prepageMinElapsed,
      viewport: input.viewport,
      viewportLoaded: input.viewportLoaded,
      showTeamsModal: input.showTeamsModal,
      showAuthModal: input.showAuthModal,
      showPricingModal: input.showPricingModal,
      userId: input.userId,
      loading: input.loading,
    },
  });
}
